import type { AppGroup, AppGroupSearchEntry } from '@openx/types/targeting/inventoryAndContent';

const APP_GROUP_OBJECT = 'appgroup';

/**
 * Maps a single normalized App Name search result (see `AppGroupSearchEntry`) into the
 * `AppGroup[]` options list consumed by `AppInventoryInput`'s autocomplete.
 *
 * The exact match (if any) is always placed first, using `inputName` as the canonical name
 * (case may differ from the DB record). Suggestions are then appended, de-duplicated by id.
 *
 * Shared between transports (GraphQL `appGroupSearch` in oa-ui, REST `/appgroup/search` in
 * unity/REST apps) — callers are responsible for normalizing their raw response into the
 * canonical `AppGroupSearchEntry` shape before calling this function.
 */
export const mapAppGroupSearchEntryToOptions = (
  entry: AppGroupSearchEntry | undefined,
  inputName: string
): AppGroup[] => {
  if (!entry) return [];

  const items: AppGroup[] = [];
  const seenIds = new Set<string>();

  if (entry.match) {
    seenIds.add(entry.match);
    items.push({
      id: entry.match,
      name: inputName,
      object: APP_GROUP_OBJECT,
    });
  }

  for (const suggestion of entry.suggestions ?? []) {
    if (seenIds.has(suggestion.id)) continue;
    seenIds.add(suggestion.id);
    items.push({
      id: suggestion.id,
      name: suggestion.name,
      object: APP_GROUP_OBJECT,
    });
  }

  return items;
};
